import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  ArrowRight,
  CheckCircle,
  ClipboardList,
  FileText,
  PhoneCall,
  QrCode,
  Search,
  ShieldCheck,
  Wifi,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { IMAGES } from "@/assets/images";

const managementBenefits = [
  {
    icon: ShieldCheck,
    title: "Management-Approved Channel",
    desc: "One official notice. No more ad-hoc calls, WhatsApp messages, or unsolicited salesperson visits.",
  },
  {
    icon: ClipboardList,
    title: "Zero Admin Workload",
    desc: "Our team handles resident enquiries, provider coordination, and installation follow-up on your behalf.",
  },
  {
    icon: FileText,
    title: "Documented & Compliant",
    desc: "All applications are tracked, documented, and follow building rules for access and installation.",
  },
];

const residentBenefits = [
  "Compare packages from multiple ISPs",
  "Submit application via a simple QR form",
  "Get follow-up updates on your application",
  "No need to contact providers individually",
  "Management-approved — no hassle entry",
  "Works for new move-ins and upgrades",
];

const steps = [
  {
    num: "01",
    icon: PhoneCall,
    title: "Management Contacts Us",
    desc: "JMB or MC reaches out to CondoWifi.my. We provide a free proposal and onboarding.",
  },
  {
    num: "02",
    icon: QrCode,
    title: "We Set Up Your Building Channel",
    desc: "A unique QR code and application link is created for your building.",
  },
  {
    num: "03",
    icon: FileText,
    title: "Notice Posted for Residents",
    desc: "Management posts the approved notice. Residents scan the QR code to begin.",
  },
  {
    num: "04",
    icon: Search,
    title: "Coverage Check & Package Comparison",
    desc: "Our team checks provider availability and presents suitable options to each resident.",
  },
  {
    num: "05",
    icon: CheckCircle,
    title: "Application & Installation Follow-Up",
    desc: "We submit the application and coordinate with the ISP until the resident is connected.",
  },
];

function FadeInSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground">

      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <div className="flex items-center gap-2 font-bold tracking-tight">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white">
              <Wifi className="h-5 w-5" />
            </div>
            <span className="text-xl">CondoWifi.my</span>
          </div>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            <a href="#management" className="transition-colors hover:text-slate-900">For Management</a>
            <a href="#residents" className="transition-colors hover:text-slate-900">For Residents</a>
            <a href="#process" className="transition-colors hover:text-slate-900">How It Works</a>
          </nav>
          <a href="https://wa.link/condowifi" target="_blank" rel="noopener noreferrer">
            <Button className="rounded-2xl">WhatsApp Us</Button>
          </a>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-slate-900 px-6 py-24 text-white lg:px-8 lg:py-32">
        {/* Background image */}
        <img
          src={IMAGES.BG_CONDO_4}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-transparent to-slate-900/70" />

        <div className="relative mx-auto grid max-w-7xl gap-16 lg:grid-cols-2 lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur">
              <Wifi className="h-4 w-4" />
              Internet Application Support for Condominiums
            </div>
            <h1 className="text-4xl font-bold tracking-tight md:text-6xl">
              One Approved Internet Application Channel for Your Condo Residents
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-slate-300">CondoWifi.my helps JMB, MC and building management provide residents with a QR-based internet application support channel, check coverage, compare internet packages, submit applications, and arrange installation while following condo management rules.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="https://tally.so/r/VLol5y" target="_blank" rel="noopener noreferrer">
                <Button className="rounded-2xl px-6 py-6 text-base">
                  Request Free Management Proposal <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="https://wa.link/condowifi" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-2xl border-white/20 bg-white/10 px-6 py-6 text-base text-white hover:bg-white/20">
                  WhatsApp CondoWifi.my
                </Button>
              </a>
            </div>
            <p className="mt-5 text-sm text-slate-400">
              Suitable for condominiums, apartments, SOHO, serviced residences, and office towers.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Card className="rounded-3xl border-white/10 bg-white/10 shadow-2xl backdrop-blur">
              <CardContent className="p-6">
                <div className="rounded-2xl bg-white p-6 text-slate-900">
                  <QrCode className="mb-4 h-12 w-12" />
                  <h2 className="text-2xl font-bold">One QR Code for Residents</h2>
                  <p className="mt-3 text-slate-600">
                    Management displays one approved notice. Residents scan, submit details, and our team helps from coverage check to installation follow-up.
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-2">
                    <div className="flex items-center justify-center rounded-xl bg-[#f032b8] px-2 py-2">
                      <img src={IMAGES.LOGO_TIME} alt="TIME" className="h-7 w-auto object-contain" />
                    </div>
                    <div className="flex items-center justify-center rounded-xl bg-white px-2 py-2 ring-1 ring-slate-200">
                      <span className="text-xs font-bold text-[#003da5]">unifi</span>
                    </div>
                    <div className="flex items-center justify-center rounded-xl bg-white px-2 py-2 ring-1 ring-slate-200">
                      <img src={IMAGES.LOGO_MAXIS} alt="Maxis" className="h-7 w-auto object-contain" />
                    </div>
                    <div className="flex items-center justify-center rounded-xl bg-[#003da5] px-2 py-2">
                      <img src={IMAGES.LOGO_CELCOMDIGI} alt="CelcomDigi" className="h-7 w-auto object-contain" />
                    </div>
                    <div className="flex items-center justify-center rounded-xl bg-white px-2 py-2 ring-1 ring-slate-200">
                      <img src={IMAGES.LOGO_UHOME} alt="U Home 5G" className="h-7 w-auto object-contain" />
                    </div>
                    <div className="flex items-center justify-center rounded-xl bg-white px-2 py-2 ring-1 ring-slate-200">
                      <img src={IMAGES.LOGO_FRESHTEL} alt="Freshtel" className="h-7 w-auto object-contain" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* ── MANAGEMENT BENEFITS ── */}
      <section id="management" className="relative overflow-hidden px-6 py-24 lg:px-8">
        <img
          src={IMAGES.BG_CONDO_2}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/80" />
        <div className="relative mx-auto max-w-7xl">
          <FadeInSection>
            <div className="mb-16 text-center">
              <h2 className="text-3xl font-bold md:text-5xl">A Cleaner Way to Handle Resident Internet Enquiries</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                CondoWifi.my gives your building a single, management-approved channel for all ISP-related matters.
              </p>
            </div>
          </FadeInSection>
          <div className="grid gap-8 md:grid-cols-3">
            {managementBenefits.map((b, i) => (
              <FadeInSection key={b.title} delay={i * 0.1}>
                <Card className="h-full rounded-3xl border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white">
                      <b.icon className="h-7 w-7" />
                    </div>
                    <h3 className="text-xl font-bold">{b.title}</h3>
                    <p className="mt-3 text-muted-foreground">{b.desc}</p>
                  </CardContent>
                </Card>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── RESIDENTS ── */}
      <section id="residents" className="relative overflow-hidden bg-slate-50 px-6 py-24 lg:px-8">
        <img
          src={IMAGES.BG_INTERIOR_2}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-slate-50/60 to-slate-50/80" />
        <div className="relative mx-auto max-w-7xl">
          <FadeInSection>
            <div className="mb-16 text-center">
              <h2 className="text-3xl font-bold md:text-5xl">Moving In? Upgrading?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                No more calling multiple providers. Just scan the QR code in your building and we handle the rest.
              </p>
            </div>
          </FadeInSection>
          <div className="grid gap-8 lg:grid-cols-2">
            <FadeInSection>
              <Card className="h-full rounded-3xl border-0 shadow-lg">
                <CardContent className="p-8">
                  <CheckCircle className="mb-5 h-12 w-12 text-green-600" />
                  <h3 className="text-2xl font-bold">What Residents Get</h3>
                  <ul className="mt-6 space-y-3">
                    {residentBenefits.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
                        <span className="text-muted-foreground">{b}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </FadeInSection>
            <FadeInSection delay={0.1}>
              <Card className="rounded-3xl border-0 bg-slate-900 text-white shadow-xl">
                <CardContent className="p-8">
                  <Wifi className="mb-5 h-12 w-12" />
                  <h3 className="text-2xl font-bold">Providers We Can Help You Check</h3>
                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <div className="flex items-center justify-center rounded-2xl bg-[#f032b8] px-3 py-3">
                      <img src={IMAGES.LOGO_TIME} alt="TIME" className="h-8 w-auto object-contain" />
                    </div>
                    <div className="flex items-center justify-center rounded-2xl bg-white px-3 py-3">
                      <span className="text-sm font-bold text-[#003da5]">unifi</span>
                    </div>
                    <div className="flex items-center justify-center rounded-2xl bg-white px-3 py-3">
                      <img src={IMAGES.LOGO_MAXIS} alt="Maxis" className="h-8 w-auto object-contain" />
                    </div>
                    <div className="flex items-center justify-center rounded-2xl bg-[#003da5] px-3 py-3">
                      <img src={IMAGES.LOGO_CELCOMDIGI} alt="CelcomDigi" className="h-8 w-auto object-contain" />
                    </div>
                    <div className="flex items-center justify-center rounded-2xl bg-white px-3 py-3">
                      <img src={IMAGES.LOGO_UHOME} alt="U Home 5G" className="h-8 w-auto object-contain" />
                    </div>
                    <div className="flex items-center justify-center rounded-2xl bg-white px-3 py-3">
                      <img src={IMAGES.LOGO_FRESHTEL} alt="Freshtel" className="h-8 w-auto object-contain" />
                    </div>
                  </div>
                  <p className="mt-6 text-sm text-slate-400">
                    Availability depends on building coverage, provider approval, and package terms.
                  </p>
                </CardContent>
              </Card>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section id="process" className="relative overflow-hidden px-6 py-24 lg:px-8">
        <img
          src={IMAGES.BG_WIFI_2}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background/70" />
        <div className="relative mx-auto max-w-7xl">
          <FadeInSection>
            <div className="mb-16 text-center">
              <h2 className="text-3xl font-bold md:text-5xl">How It Works</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                From onboarding your building to getting residents connected — a simple 5-step process.
              </p>
            </div>
          </FadeInSection>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {steps.map((s, i) => (
              <FadeInSection key={s.num} delay={i * 0.08}>
                <Card className="h-full rounded-3xl border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="mb-4 text-4xl font-black text-slate-200">{s.num}</div>
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-bold">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
                  </CardContent>
                </Card>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── REQUIREMENTS & DISCLAIMER ── */}
      <section className="relative overflow-hidden bg-slate-50 px-6 py-24 lg:px-8">
        <img
          src={IMAGES.BG_CONDO_3}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-slate-50/60 to-slate-50/80" />
        <div className="relative mx-auto max-w-7xl">
          <FadeInSection>
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold md:text-4xl">Before We Begin</h2>
            </div>
          </FadeInSection>
          <div className="grid gap-8 lg:grid-cols-2">
            <FadeInSection>
              <Card className="h-full rounded-3xl border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="mb-5 text-xl font-bold">Management Requirements</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    {[
                      "JMB or MC authorisation to display resident notices",
                      "Access permission for ISP technicians (escorted or scheduled)",
                      "Designated location for duct or cable routing (if applicable)",
                      "Agreement to CondoWifi.my terms of service",
                    ].map((r) => (
                      <li key={r} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </FadeInSection>
            <FadeInSection delay={0.1}>
              <Card className="h-full rounded-3xl border-0 bg-amber-50 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="mb-5 text-xl font-bold text-amber-800">Important Disclaimer</h3>
                  <ul className="space-y-3 text-amber-700">
                    {[
                      "CondoWifi.my is an independent support service and is not affiliated with any ISP.",
                      "Coverage and package availability are subject to provider confirmation.",
                      "Installation timelines depend on ISP scheduling and building access.",
                      "We do not guarantee approval of any internet application.",
                    ].map((d) => (
                      <li key={d} className="flex items-start gap-3">
                        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                        <span className="text-sm">{d}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-slate-900 px-6 py-24 text-white lg:px-8">
        <FadeInSection>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold md:text-5xl">Invite CondoWifi.my to Your Building</h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-slate-300">
              Join building management teams across Malaysia who have simplified resident internet applications with one approved channel.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a href="https://tally.so/r/VLol5y" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="rounded-2xl px-8 py-6 text-base">
                  Get Free Proposal <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="https://wa.link/condowifi" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="rounded-2xl border-white/20 bg-white/10 px-8 py-6 text-base text-white hover:bg-white/20">
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-slate-200 bg-white px-6 py-10 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 text-sm text-slate-500 md:flex-row">
          <div className="flex items-center gap-2 font-bold text-slate-900">
            <div className="flex h-7 w-7 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Wifi className="h-4 w-4" />
            </div>
            CondoWifi.my
          </div>
          <p>© 2026 CondoWifi.my · Internet Application Support for Malaysian Condominiums</p>
          <a href="https://wa.link/condowifi" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">
            WhatsApp: 017-4706686
          </a>
        </div>
      </footer>
    </div>
  );
}
